import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IonContent, Platform, PopoverController } from '@ionic/angular';
import { SelectListComponent } from 'src/app/components/select-list/select-list.component';
import { ApiService } from 'src/app/services/api.service';
import { GlobalDataService } from 'src/app/services/global-data.service';
import { JsonLdService } from 'src/app/services/json-ld.service';
import { SeoService } from 'src/app/services/seo.service';
import { CategoryItem } from 'src/app/types/category';
import { PostItem, PostListingData } from 'src/app/types/post';
import { SEOSchema } from 'src/app/types/seo';
import { removeTags } from 'src/app/utils/string-transform';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-competitions-listing',
  templateUrl: './competitions-listing.page.html',
  styleUrls: ['./competitions-listing.page.scss'],
})
export class CompetitionsListingPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  lastItem;
  amountPerPage: number = 6;
  xhrInProgress: boolean = false;
  hasMoreItems: boolean = true;
  selectedCategory: CategoryItem;
  featuredItem: PostItem;
  data: PostListingData = {
    title: 'Competitions',
    excerpt:
      environment.environment_name == 'capegate'
        ? 'You could win with Capegate or one of our stores. View our exciting competition(s) below, and be sure to get your entry in. Today could be your lucky day!'
        : environment.environment_name == 'somerset'
        ? 'You could win with Somerset Mall or one of our stores. View our exciting competition(s) below, and be sure to get your entry in.'
        : environment.environment_name == 'glen'
        ? 'You could win with The Glen. View our exciting competition(s), & get your entry in.Contact stores directly for relevant competition information'
        : environment.environment_name == 'rosebank'
        ? 'You could win with Rosebank Mall or with one of our stores. View our exciting competition(s) below, today could be your lucky day!'
        : environment.environment_name == 'woodlands'
        ? 'You could win with Woodlands Boulevard! View our exciting competition(s) and start entering! Please contact stores directly for all competition information.'
        : `You could win with ${environment.environment_title} or one of our stores. View our exciting competition(s) below, and be sure to get your entry in. Who knows, today could be your lucky day! If you've got a competitive streak, or are just feeling lucky, give it a go and enter. Store competitions are not hosted by ${environment.environment_title}. Please <a href='/shops'>contact stores</a> directly for all relevant competition information.`,
  };

  schema: SEOSchema = {
    '@context': 'http://schema.org',
    '@type': 'ItemList',
    name: environment.environment_title,
    url: environment.environment_url,
  };

  sliceStart: number = 0;

  constructor(
    private platform: Platform,
    private globalData: GlobalDataService,
    private seo: SeoService,
    private API: ApiService,
    private route: ActivatedRoute,
    public popoverController: PopoverController,
    private jsonLdService: JsonLdService
  ) {
    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');
    });
  }

  /**
   * Fetch more data according to page & filter
   */
  fetchData() {
    this.API.GET(
      `public/competitions/${environment.mall_id}?featured=1&amount=${
        this.amountPerPage
      }${
        this.lastItem
          ? '&lastSlug=' +
            this.lastItem.slug +
            '&lastPublishedDate=' +
            this.lastItem.publishedAt
          : ''
      }${
        this.selectedCategory ? '&category=' + this.selectedCategory.slug : ''
      }`
    ).subscribe({
      next: (response) => {
        if (!this.data.items || this.data.items.length < 1) {
          let items = response.results;
          if (response.results[0]?.featured) {
            this.featuredItem = response.results[0];
            this.sliceStart = 1;
          } else {
            this.featuredItem = null;
            this.sliceStart = 0;
          }

          this.data = {
            ...this.data,
            categories: response.categories,
            items: items,
          };
        } else {
          const extendedItems = this.data.items.concat(response.results);
          this.data.items = extendedItems;
        }

        if (this.selectedCategory?.slug && !this.selectedCategory?.title) {
          this.selectedCategory = response.categories.filter((category) => {
            return category.slug === this.selectedCategory.slug;
          })[0];
        }

        if (this.selectedCategory) this.setCategorySeo();

        this.hasMoreItems = !(response.results.length < this.amountPerPage);

        this.xhrInProgress = false;
      },
      error: (response) => {},
    });
  }

  /**
   * Load more results
   */
  loadMore() {
    if (!this.xhrInProgress) {
      this.xhrInProgress = true;

      this.lastItem = this.data.items[this.data.items.length - 1];

      this.fetchData();
    }
  }

  /**
   * Reset category filter
   */
  resetCategories() {
    delete this.data.items;
    this.selectedCategory = null;
    this.lastItem = null;
    this.fetchData();
    window?.history.pushState({}, '', '/competitions');

    // Set default Mall SEO data
    this.setMallSeo(this.globalData.getGlobalData());
  }

  /**
   * Present category filter popover
   */
  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: SelectListComponent,
      componentProps: {
        data: this.data.categories,
      },
      event: ev,
      translucent: true,
    });

    popover.onWillDismiss().then((popoverData) => {
      if (popoverData.data === 'all') {
        this.resetCategories();
      } else {
        const filterCategory = {
          title: popoverData.data?.title,
          slug: popoverData.data?.slug,
          seoTitle: popoverData.data?.seoTitle,
          seoDescription: popoverData.data?.seoDescription,
        };
        if (popoverData.data && this.selectedCategory != filterCategory) {
          this.selectedCategory = filterCategory;

          if (this.selectedCategory?.slug) {
            delete this.data.items;
            delete this.lastItem;
            this.selectedCategory = popoverData.data;
            this.fetchData();
            window?.history.pushState(
              {},
              '',
              `/competitions/category/${this.selectedCategory.slug}`
            );
          }
        }
      }
    });

    return await popover.present();
  }

  ngOnInit() {
    const initialMallData = this.globalData.getGlobalData();

    this.route.data.subscribe((response: any) => {
      let items = response.data.results;
      if (response.data.results[0]?.featured) {
        this.featuredItem = response.data.results[0];
        this.sliceStart = 1;
      }

      this.data = {
        ...this.data,
        categories: response.data.categories,
        items: items,
      };

      this.hasMoreItems = !(this.data.items.length < this.amountPerPage);

      let categorySlug = this.route.snapshot.paramMap.get('categorySlug');
      if (categorySlug) {
        this.selectedCategory = {
          slug: categorySlug,
        };
      } else {
        this.selectedCategory = null;
      }

      if (this.selectedCategory?.slug && !this.selectedCategory?.title) {
        for (let i = 0; i < this.data.categories.length; i++) {
          if (this.data.categories[i].slug === this.selectedCategory.slug) {
            this.selectedCategory = this.data.categories[i];
            break;
          } else if (this.data.categories[i].children) {
            for (let k = 0; k < this.data.categories[i].children.length; k++) {
              if (
                this.data.categories[i].children[k].slug ===
                this.selectedCategory.slug
              ) {
                this.selectedCategory = this.data.categories[i].children[k];
                break;
              }
            }
          }
        }
      }

      // Set SEO data
      if (this.selectedCategory) {
        this.setCategorySeo();
      } else {
        this.setMallSeo(initialMallData);
      }
    });
  }

  setMallSeo(mallData) {
    // Set Meta & Social tags
    if (!this.cordova) {
      let schemaData = { ...this.schema };

      if (this.data?.items?.length > 0) {
        let itemListElement = [];

        for (let index = 0; index < this.data.items.length; index++) {
          itemListElement.push({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Competition',
              name: this.data.items[index].title,
              image: this.data.items[index].featuredImage,
              brand: {
                logo: this.data.items[index].featuredImage,
                name: this.data.items[index].title,
              },
            },
          });
        }

        schemaData = {
          ...schemaData,
          numberOfItems: this.data.items.length,
          itemListElement: itemListElement,
        };
      }

      // Title tag
      const seoTitle =
        'Competitions | ' + environment.environment_title + ' Shopping Centre';
      this.seo.setTitle(seoTitle);
      schemaData = {
        ...schemaData,
        name: seoTitle,
      };

      // Description tag
      const seoData = {
        title: seoTitle,
        description: removeTags(this.data.excerpt),
        keywords: mallData.general.metaKeywords + ' competitions',
        socialImage: mallData.social.openGraphImage,
      };
      schemaData = {
        ...schemaData,
        description: seoData.description,
      };
      this.seo.setMeta(seoData);
      this.seo.setSEOMeta(seoData);
      this.schema = { ...this.schema, ...schemaData };
      this.jsonLdService.insertSchema(this.schema);
    }
  }

  setCategorySeo() {
    let schemaData = { ...this.schema };

    const seoData = {
      title:
        'Competitions | ' +
        (this.selectedCategory.seoTitle &&
        this.selectedCategory.seoTitle.length > 0
          ? this.selectedCategory.seoTitle
          : this.selectedCategory.title) +
        ' | ' +
        environment.environment_title +
        ' Shopping Centre',
      description: this.selectedCategory.seoDescription,
    };
    this.seo.setTitle(seoData.title);
    schemaData = {
      ...schemaData,
      name: seoData.title,
    };
    this.seo.setMeta(seoData);
    this.seo.setSEOMeta(seoData);
    this.schema = { ...this.schema, ...schemaData };

    this.jsonLdService.insertSchema(this.schema);
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
  }
}
